import type { MutableRefObject } from 'react';
import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	UPDATE_TASK_ERROR_MAP,
	useUpdatePersonalizedPetitionTask,
} from 'src/app/pages/starterDashboardV2/shared/hooks';

import { TASKS_CONFIGS } from '../../../../constants';

import { isKnownStarterTaskCompletedOnPetition, parsePersonalizedTasks } from './taskShapes';

export type PendingCtaSync = Readonly<{ personalizedTaskId: string; featureKey: string }>;

export function usePendingPersonalizedTaskSync(
	petition: StarterDashboardV2PageData['petition'],
	pendingRef: MutableRefObject<PendingCtaSync | null>,
	clearPending: () => void,
	refreshData: () => void,
): void {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const { mutate: updatePersonalizedTask } = useUpdatePersonalizedPetitionTask({
		onSuccess: () => {
			refreshData();
		},
		onError: (error) => {
			if (error === UPDATE_TASK_ERROR_MAP.UNAUTHENTICATED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
	});

	useEffect(() => {
		const pending = pendingRef.current;
		if (!pending) return;

		if (!Object.hasOwn(TASKS_CONFIGS, pending.featureKey)) {
			clearPending();
			return;
		}

		if (!isKnownStarterTaskCompletedOnPetition(petition, pending.featureKey)) return;

		const { tasks } = parsePersonalizedTasks(petition.personalizedTasks);
		const row = tasks.find((t) => t.id === pending.personalizedTaskId);
		if (!row || row.status !== 'ACTIVE') {
			clearPending();
			return;
		}

		clearPending();
		void updatePersonalizedTask({
			petitionId: petition.id,
			personalizedPetitionTaskId: pending.personalizedTaskId,
			status: 'COMPLETED',
		});
	}, [petition, clearPending, updatePersonalizedTask]);
}
